<template>
    <HeaderComponent />
    <main class="vue-view scrollable" v-if="component != null">
        <OrderAndPayView
            :tableId="component.props.tableId"
            :roomId="component.props.roomId"
            :billId="component.props.billId"
            :externalPaymentId="component.props.externalPaymentId"
            :transactionId="component.props.transactionId"
            :transactionFor="component.props.transactionFor"
        />
    </main>

    <template v-if="isProductionViewVisible">
        <Teleport to=".view.production">
            <ProductionView />
        </Teleport>
    </template>

    <template v-if="isOrderListViewVisible">
        <Teleport to=".view.orders">
            <OrderListView />
        </Teleport>
    </template>

    <template v-if="isItemListViewVisible">
        <Teleport to=".view.items">
            <ItemListView />
        </Teleport>
    </template>

    <template v-if="isPaymentListViewVisible">
        <Teleport to=".view.payments">
            <PaymentListView />
        </Teleport>
    </template>

    <template v-if="isOpenBillsViewVisible">
        <Teleport to=".view.open-bills">
            <OpenBillsView />
        </Teleport>
    </template>

    <template v-if="isRoomPlanViewVisible">
        <Teleport to=".view.room-plan">
            <RoomPlanView
                :showSettingsRoomPlan="roomPlanViewProps?.showSettingsRoomPlan"
                :roomId="roomPlanViewProps?.roomId"
                :transferBillOptions="roomPlanViewProps?.transferBillOptions"
            />
        </Teleport>
    </template>
</template>

<script setup lang="ts">
    import { setPrimevueLocale } from "@/components/language/primevue/primevueLocale";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { ShowRoomPlanEventData } from "@/data/model/Table";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { useOfferStore } from "@/data/stores/offer";
    import { useOperatorSettingsStore } from "@/data/stores/operatorSettings";
    import { useOrderedArticlesStore } from "@/data/stores/orderedArticles";
    import { usePaymentStore } from "@/data/stores/payment";
    import { usePrintStore } from "@/data/stores/print";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Logging } from "@/data/util/Logging";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { clientLanguage } from "@/shared/localization/language";
    import { onBeforeMount, ref } from "vue";

    import HeaderComponent from "@/operator/components/layout/HeaderComponent.vue";
    import ItemListView from "@/operator/views/ItemListView.vue";
    import OpenBillsView from "@/operator/views/OpenBillsView.vue";
    import OrderAndPayView from "@/operator/views/OrderAndPayView.vue";
    import OrderListView from "@/operator/views/OrderListView.vue";
    import PaymentListView from "@/operator/views/PaymentListView.vue";
    import ProductionView from "@/operator/views/ProductionView.vue";
    import RoomPlanView from "@/operator/views/RoomPlanView.vue";

    // stores
    useBillStore();
    useCartStore();
    useOfferStore();
    usePaymentStore();
    useTableAllocationStore();
    useOperatorSettingsStore();
    usePrintStore();
    const orderedArticles = useOrderedArticlesStore();

    // refs
    const component = ref(null as any);
    const isProductionViewVisible = ref(false);
    const isOpenBillsViewVisible = ref(false);
    const isRoomPlanViewVisible = ref(false);
    const isOrderListViewVisible = ref(false);
    const isItemListViewVisible = ref(false);
    const isPaymentListViewVisible = ref(false);

    const roomPlanViewProps = ref<ShowRoomPlanEventData>();

    // template refs
    let lastTimeOpenedOrderAndPay = new Date().getTime();

    onBeforeMount(() => {
        operatorEvents.hideVisibleVueView.add(() => {
            $("#vue-app").removeClass("shown");
            component.value = null;
        }, this);

        operatorEvents.showOrderAndPayView.add((eventData) => {
            // todo: to prevent multiple calls in short time
            if (eventData.force !== true && new Date().getTime() - lastTimeOpenedOrderAndPay < 300) {
                Logging.LogWarning(
                    "Preventing multiple calls to showOrderAndPayView. Listeners num: " +
                        operatorEvents.showOrderAndPayView.getNumListeners()
                );
                return;
            }
            lastTimeOpenedOrderAndPay = new Date().getTime();

            $("#vue-app").addClass("shown");

            component.value = {
                props: {
                    tableId: eventData.tableId,
                    roomId: undefined,
                    billId: eventData.billId,
                    externalPaymentId: eventData.externalPaymentId,
                    transactionId: eventData.transactionId,
                    transactionFor: eventData.transactionFor,
                },
            };
            operatorEvents.hideVisibleController.dispatch();
        }, this);

        operatorEvents.hideVisibleController.add(() => {
            //hide other controllers
            const activeCtrls = $(".app > .view.shown:not(#vue-app)");
            if (activeCtrls.length == 0) return;

            //remove all transitions
            activeCtrls.addClass("notransition shown").removeClass("animleft animright fadetransition fadeout")[0]
                .offsetHeight;

            activeCtrls.addClass("fadetransition")[0].offsetHeight;
            activeCtrls.removeClass("notransition shown").addClass("fadeout");

            activeCtrls.data("ctrl").onBecomeInvisible();
        }, this);

        operatorEvents.productionController.onInit.add(() => {
            isProductionViewVisible.value = true;
        });

        operatorEvents.openBillsController.onShown.add(() => {
            isOpenBillsViewVisible.value = true;
        });

        operatorEvents.openBillsController.onHidden.add(() => {
            isOpenBillsViewVisible.value = false;
        });

        operatorEvents.itemsController.onShown.add(() => {
            isItemListViewVisible.value = true;
        });

        operatorEvents.itemsController.onHidden.add(() => {
            isItemListViewVisible.value = false;
        });

        operatorEvents.paymentsController.onShown.add(() => {
            isPaymentListViewVisible.value = true;
        });

        operatorEvents.paymentsController.onHidden.add(() => {
            isPaymentListViewVisible.value = false;
        });

        operatorEvents.ordersController.onShown.add(() => {
            isOrderListViewVisible.value = true;
        });

        operatorEvents.ordersController.onHidden.add(() => {
            isOrderListViewVisible.value = false;
        });

        operatorEvents.roomPlanController.onShown.add((ev?: ShowRoomPlanEventData) => {
            roomPlanViewProps.value = ev;
            isRoomPlanViewVisible.value = true;
        });

        operatorEvents.roomPlanController.onHidden.add(() => {
            isRoomPlanViewVisible.value = false;
        });

        operatorEvents.context.settingsUpdated.add((settings) => {
            const settingsStore = useOperatorSettingsStore();
            settingsStore.setOperatorSettings(settings);
        });

        // the same subscriptions as in the app's App.vue - this root has stores of its own
        dataEvents.appSocket.BillOpened.add(orderedArticles.onBillEvent);
        dataEvents.appSocket.BillChangedShared.add(orderedArticles.onBillEvent);
        dataEvents.appSocket.BillClosed.add(orderedArticles.onBillEvent);
    });

    setPrimevueLocale(clientLanguage());
</script>

<style lang="scss">
    #vue-app {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
        z-index: 100;
        opacity: 0;
        background-color: #fff;

        &.shown {
            z-index: 102;
            opacity: 1;
        }
    }

    .vue-view {
        position: absolute;
        top: 52px;
        right: 0;
        bottom: 0;
        left: 0;

        display: flex;
    }

    .view-container {
        position: absolute;
        top: 52px;
        right: 0;
        bottom: 0;
        left: 0;

        &.has-footer {
            bottom: 42px;
        }
    }
</style>
